/**
 * @fsd_page SelectionItem - список ресторанов в подборке
 */

import s from './SelectionItem.module.sass'

import { FC, useEffect } from 'react'
import { useAppDispatch, useAppSelector, useInjectPageReducer } from '@app/model/store/client'

import { loadMorePage, loadPage } from '@pages/SelectionItem/model/thunks'
import SelectionItemReducer from '@pages/SelectionItem/model/slice'

import { CompilationGrid } from '@widgets/News/CompilationGrid'
import { RestCard, RestCardSkeleton } from '@widgets/Restaurant/RestCards/RestCard'
import { SelectionCardCompilation, SelectionCardCompilationSkeleton } from '@widgets/Selection/SelectionCardCompilation'

import { Favorite } from '@features/Favorite'
import { Pagination } from '@features/Common/Pagination'

import { ContainerLongLeft } from '@shared/ui/Layout/Containers/ContainerLongLeft'
import { ContainerTwoColumns } from '@shared/ui/Layout/Containers/ContainerTwoColumns'
import { Cover, CoverSkeleton } from '@shared/ui/Layout/Cover'
import { SectionCarousel } from '@shared/ui/Layout/SectionCarousel'
import { Text } from '@shared/ui/Typography/Text'
import { Title } from '@shared/ui/Typography/Title'

import { AdvertTemplate, SidebarSticky } from '@shared/ui/Layout/AdvertTemplate'
import { BlockBoundary } from '@shared/lib/components/BlockBoundary'
import { EGG } from '@shared/api/analytics'
import { IconButton } from '@shared/ui/Actions/IconButton'
import { numWord } from '@shared/lib/helpers/numWord'
import { Rating } from '@shared/ui/Feedback/Rating'
import { shuffle } from '@shared/lib/helpers/shuffle'
import { targetStickersSelection } from '@shared/api/middleware/mappingAPI/patchStickersSelections'
import { useClientResponsive } from '@shared/lib/hooks/useClientResponsive'
import SectionTitle from '@shared/ui/Layout/SectionTitle/SectionTitle'
import type { SelectionItemServerData } from '@pages/SelectionItem/model/types'

import IconLike from '@icons/icon-like.svg'

const viewFromServer = (initialData: SelectionItemServerData) => ({
  selection: {
    data: initialData.selection.description,
    restaurants: initialData.selection.restaurants ?? null,
    loading: { page: false, items_full: false, items_addition: false },
    pagination: initialData.selection.pagination ?? null,
    paramsRequest: initialData.selection.requestParams,
  },
  recommended: initialData.recommendation ?? null,
  afishaSelected: initialData.afishaSelected ?? null,
})

const SelectionItem: FC<{ initialData?: SelectionItemServerData | null }> = ({ initialData = null }) => {
  useInjectPageReducer('selection_item', SelectionItemReducer)

  const dispatch = useAppDispatch()

  const currentCity = useAppSelector((state) => state.global.currentCity)
  const storeSelection = useAppSelector((state) => state.pages?.selection_item)
  const storedView = storeSelection?.selection.data ? storeSelection : null
  const serverView = initialData ? viewFromServer(initialData) : null
  const view = storedView ?? serverView
  const selection = view?.selection
  const recommended = view?.recommended ?? null
  const afishaSelected = view?.afishaSelected ?? null
  const restaurants = selection?.restaurants
  const pagination = selection?.pagination
  const data = selection?.data
  const { isMobile, isTablet } = useClientResponsive()

  // const nextPageNumber = pagination?.page.next.number
  const currentPageNumber = pagination?.page.current.number

  const isAllPositionsSold = data?.categories?.advert?.active
  const restaurantsFormatted =
    isAllPositionsSold && Array.isArray(restaurants) && restaurants.length > 0 ? shuffle(restaurants) : restaurants
  const imgAlt = data?.description?.title
    ? `Фото подборки от Афиши — ${data.description.title}`
    : `Фото подборки от Афиши`

  let bigCoverImage = ''
  if (data?.img?.sizes) {
    bigCoverImage = isMobile ? data.img.sizes.desktop.vertical || '' : data.img.sizes.desktop.horizontal || ''
  }

  const targetStickersSlugs: string[] = Object.values(targetStickersSelection)
  const isTargetSlugForStickersSelections = Boolean(data?.url?.slug && targetStickersSlugs.includes(data.url.slug))

  if (isTargetSlugForStickersSelections && data?.img?.icon) {
    // если подборка в списке целевых, то используем иконку
    bigCoverImage = data?.img?.icon
  }

  useEffect(() => {
    if (!data?.id) return
    EGG.pages.SelectionItem.appear_screen_page()
  }, [data?.id])

  if (!data || !selection) return null

  const listRestSkeleton = Array.from(Array(6).keys())

  const isShowRecommendedRests = selection.loading.page || (!!recommended && !selection.loading.page)
  const isShowAfishaSelected = selection.loading.page || (!!afishaSelected && !selection.loading.page)

  return (
    <BlockBoundary>
      <main className={s.main}>
        {selection.loading.page ? (
          <CoverSkeleton classNameContainer={s.cover} />
        ) : (
          <Cover img={bigCoverImage} classNameContainer={s.cover}>
            <Cover.Main className={s['cover-main']} alt={imgAlt}>
              {isAllPositionsSold && !isMobile && <Rating isPromo sizes={'M S'} className={s.promo} />}

              {/*{data.date && (*/}
              {/*  <Text tag={'span'} sizes={'M S'} className={s.date}>*/}
              {/*    {data.date}*/}
              {/*  </Text>*/}
              {/*)}*/}

              <Title sizes={'h1 h3'} className={s.title}>
                {data?.description?.title}
              </Title>

              <div className={s['cover-additional']}>
                <Text tag={'span'} sizes={'M S'}>
                  Выбор Афиши
                </Text>
                {data.description.restaurantsCount > 0 && (
                  <Text tag={'span'} sizes={'M S'}>
                    {data.description.restaurantsCount}{' '}
                    {numWord(data.description.restaurantsCount, ['ресторан', 'ресторана', 'ресторанов'])}
                  </Text>
                )}
              </div>
            </Cover.Main>

            <Cover.Aside alt={imgAlt}>
              {isAllPositionsSold && isMobile && <Rating isPromo sizes={'M S'} className={s.promo} />}

              <Favorite
                category={'selection'}
                id={data.id}
                analytic={{
                  data,
                  other: { section_name: 'page' },
                }}
                className={s.favorite}>
                <IconButton icon={IconLike} sizes='XL M' mode='transparent' />
              </Favorite>
            </Cover.Aside>
          </Cover>
        )}

        <ContainerTwoColumns>
          <ContainerTwoColumns.Main>
            {!!data?.description && (
              <Text sizes={'XL M'} paragraph className={s.description}>
                {data?.description.description}
              </Text>
            )}

            <div className={s['restaurants-list']} data-loading={selection.loading.items_full}>
              {restaurantsFormatted?.length
                ? restaurantsFormatted?.map((rest, index) => (
                    <RestCard
                      key={rest.id}
                      data={rest}
                      sizes={'L L L S'}
                      className={s['restaurants-item']}
                      type={'selection'}
                      analytic={{
                        advertCardPlaceType: 'selection',
                        section_name: `Подборка - ${data?.description.title}`,
                        section_index: index,
                      }}
                    />
                  ))
                : listRestSkeleton.map((_el, index) => (
                    <RestCardSkeleton
                      key={index}
                      className={s['restaurants-item']}
                      sizes={'L L L S'}
                      type={'selection'}
                    />
                  ))}
            </div>

            {!!restaurantsFormatted?.length && (
              <Pagination
                mode={'all'}
                autoloadingOnMobile={true}
                currentPage={currentPageNumber ?? 0}
                pageCount={pagination?.page.count}
                handleLoadMore={() => dispatch(loadMorePage())}
                handleSelectPage={(page: number) => dispatch(loadPage(page))}
                analytic={{ section_name: 'Подборка' }}
                className={s.pagination}
              />
            )}

            {isShowRecommendedRests && (
              <SectionCarousel
                countSlidesOnContainer={3}
                title={'Рекомендации для вас'}
                className={s.recommended}
                skeleton={selection.loading.page}>
                <SectionCarousel.Items>
                  {recommended?.map((rest, index) => (
                    <RestCard
                      key={rest.id}
                      data={rest}
                      sizes={'L L ML M'}
                      type={'list'}
                      analytic={{
                        advertCardPlaceType: null,
                        section_name: `Рекомендации`,
                        section_index: index,
                      }}
                    />
                  )) || []}
                </SectionCarousel.Items>
                <SectionCarousel.Skeletons>
                  {Array.from(Array(4).keys()).map((_i, idx) => (
                    <RestCardSkeleton key={idx} sizes={'L L ML M'} type={'list'} />
                  ))}
                </SectionCarousel.Skeletons>
              </SectionCarousel>
            )}

            <AdvertTemplate type={'100%x240 Footer'} className={s.advert} />
          </ContainerTwoColumns.Main>

          {!isMobile && !isTablet && (
            <ContainerTwoColumns.Aside>
              <AdvertTemplate type={'240x400'} container={SidebarSticky} />
              <AdvertTemplate type={'240x400_2nd'} container={SidebarSticky} />
            </ContainerTwoColumns.Aside>
          )}
        </ContainerTwoColumns>

        {isShowAfishaSelected && (
          <ContainerLongLeft classNameContent={s['afisha-selected']}>
            {/* Выбор Афиши */}
            <h2 className='visually-hidden'>Выбор Афиши, app home slider-roll, selection-card</h2>

            {isMobile ? (
              <SectionCarousel
                title={'Выбор Афиши'}
                link={currentCity?.slug ? `/${currentCity.slug}/restaurants/afisha_selected/` : ''}
                countSlidesOnContainer={3}
                skeleton={selection.loading.page}>
                <SectionCarousel.Items>
                  {(afishaSelected ?? []).map((item, index) => (
                    <SelectionCardCompilation
                      key={item.id}
                      data={item}
                      sizes={'M1x1'}
                      analytic={{
                        section_name: `Выбор Афиши`,
                        section_index: index,
                      }}
                    />
                  ))}
                </SectionCarousel.Items>

                <SectionCarousel.Skeletons>
                  {Array.from(Array(6).keys()).map((_i, idx) => (
                    <SelectionCardCompilationSkeleton key={idx} sizes={'M1x1'} />
                  ))}
                </SectionCarousel.Skeletons>
              </SectionCarousel>
            ) : (
              <section className={s['home-selections-grid']}>
                <SectionTitle
                  title={'Выбор Афиши'}
                  link={currentCity?.slug ? `/${currentCity.slug}/restaurants/afisha_selected/` : ''}
                  skeleton={selection.loading.page}
                />

                <CompilationGrid
                  counts={'6 6 4'}
                  type={'selections'}
                  list={afishaSelected || []}
                  analytic={{
                    section_name: `Выбор Афиши`,
                  }}
                  skeleton={selection.loading.page}
                />
              </section>
            )}
          </ContainerLongLeft>
        )}

        {!isMobile && !isTablet && (
          <AdvertTemplate type={'Content1'} container={ContainerLongLeft} className={s.advert} />
        )}
      </main>
    </BlockBoundary>
  )
}

export default SelectionItem
